<template>
  <div class="find-srf-consultant-app">
    <div v-if="isLoading" class="find-srf-consultant-app__status" aria-live="polite">
      <LoadingSpinner :label="loadingLabel" />
      <p class="find-srf-consultant-app__loading-text">{{ loadingLabel }}</p>
    </div>

    <p v-else-if="errorMessage" class="find-srf-consultant-app__status error-component" role="alert">
      {{ errorMessage }}
    </p>

    <template v-else-if="appData">
      <form class="find-srf-consultant-app__filters" @submit.prevent="applySearch">
        <div class="find-srf-consultant-app__search-row input-text input-text--search">
          <div class="find-srf-consultant-app__search-field-wrapper">
            <input
              id="consultant-search"
              v-model="draftSearch"
              class="find-srf-consultant-app__search-field input-text__input input-text__input--search"
              type="search"
              role="combobox"
              autocomplete="off"
              aria-autocomplete="list"
              :aria-controls="isSuggestionListOpen && searchSuggestions.length ? 'consultant-search-suggestions' : undefined"
              :aria-activedescendant="activeSuggestionIndex >= 0 ? `consultant-search-suggestion-${activeSuggestionIndex}` : undefined"
              :aria-expanded="isSuggestionListOpen && searchSuggestions.length > 0"
              :placeholder="translations.searchPlaceholder"
              :aria-label="translations.searchPlaceholder"
              @focus="isSuggestionListOpen = true"
              @input="handleSuggestionInput"
              @search="applySearchFromInput"
              @blur="closeSuggestions"
              @keydown.down.prevent="moveSuggestion(1)"
              @keydown.up.prevent="moveSuggestion(-1)"
              @keydown.enter="selectActiveSuggestion"
            />
            <ul
              v-if="searchSuggestions.length && isSuggestionListOpen"
              id="consultant-search-suggestions"
              class="find-srf-consultant-app__suggestions"
              role="listbox"
            >
              <li
                v-for="(suggestion, index) in searchSuggestions"
                :id="`consultant-search-suggestion-${index}`"
                :key="`${suggestion.type}-${suggestion.value}`"
                class="find-srf-consultant-app__suggestion"
                :class="{ 'find-srf-consultant-app__suggestion--active': activeSuggestionIndex === index }"
                role="option"
                :aria-selected="activeSuggestionIndex === index"
                @mousedown.prevent="selectSuggestion(suggestion.value)"
              >
                <span class="find-srf-consultant-app__suggestion-type">{{ suggestion.type }}</span>
                {{ suggestion.value }}
              </li>
            </ul>
          </div>
          <button class="button find-srf-consultant-app__search-button" type="submit">
            {{ translations.searchConsultant }}
          </button>
        </div>

        <div class="find-srf-consultant-app__filter-options">

          <fieldset class="find-srf-consultant-app__show-only">
            <legend class="find-srf-consultant-app__show-only-heading">
              {{ translations.showOnly }}
            </legend>

            <div class="find-srf-consultant-app__checkbox checkbox">
              <input
                id="authorized-salary-consultants"
                v-model="onlyAuthorizedSalaryConsultants"
                class="checkbox__input"
                type="checkbox"
              />
              <label class="checkbox__label" for="authorized-salary-consultants">
                {{ translations.authorizedSalaryConsultantFilter }}
              </label>
            </div>

            <div class="find-srf-consultant-app__checkbox checkbox">
              <input
                id="authorized-accounting-consultants"
                v-model="onlyAuthorizedAccountingConsultants"
                class="checkbox__input"
                type="checkbox"
              />
              <label class="checkbox__label" for="authorized-accounting-consultants">
                {{ translations.authorizedAccountingConsultantFilter }}
              </label>
            </div>

            <div class="find-srf-consultant-app__checkbox checkbox">
              <input
                id="certified-business-advisors"
                v-model="onlyCertifiedBusinessAdvisors"
                class="checkbox__input"
                type="checkbox"
              />
              <label class="checkbox__label" for="certified-business-advisors">
                {{ translations.certifiedBusinessAdvisorFilter }}
              </label>
            </div>

            <div class="find-srf-consultant-app__checkbox checkbox">
              <input
                id="authorized-agencies"
                v-model="onlyAuthorizedAgencies"
                class="checkbox__input"
                type="checkbox"
              />
              <label class="checkbox__label" for="authorized-agencies">
                {{ translations.authorizedAgencyFilter }}
              </label>
            </div>
          </fieldset>

          <button
            v-if="hasActiveFilters"
            class="find-srf-consultant-app__clear-button link link--with-icon"
            type="button"
            @click="clearFilters"
          >
            <span class="find-srf-consultant-app__clear-button-icon icon icon--close" aria-hidden="true"></span>
            {{ translations.clearFilters }}
          </button>
        </div>
      </form>

      <section
        class="find-srf-consultant-app__results"
        :aria-labelledby="hasAppliedSearchOrFilter ? 'consultant-results-heading' : undefined"
      >
        <header v-if="hasAppliedSearchOrFilter && appData.consultants.length" class="find-srf-consultant-app__results-header">
          <h2 id="consultant-results-heading" class="find-srf-consultant-app__results-heading">
            {{ translations.searchResults }}
          </h2>
          <p class="find-srf-consultant-app__results-summary" aria-live="polite">
            {{ resultsSummary }}
          </p>
          <div class="find-srf-consultant-app__mobile-sort dropdown">
            <label for="consultant-sort">{{ translations.sortBy }}</label>
            <select id="consultant-sort" v-model="selectedSort" class="dropdown__select">
              <option v-if="!selectedSort" disabled value="">{{ translations.sortBy }}</option>
              <option
                v-for="option in appData.sortOptions"
                :key="`${option.key}-${option.direction}`"
                :value="`${option.key}-${option.direction}`"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </header>

        <div v-if="hasAppliedSearchOrFilter && appData.consultants.length" class="find-srf-consultant-app__list">
          <div class="find-srf-consultant-app__columns">
            <div
              v-for="column in columns"
              :key="column.key"
              class="find-srf-consultant-app__column"
              :class="`find-srf-consultant-app__column--${column.key}`"
            >
              <button
                class="find-srf-consultant-app__sort-button"
                type="button"
                :aria-label="sortLabel(column.label, column.key)"
                @click="sortBy(column.key)"
              >
                {{ column.label }}
                <span
                  class="find-srf-consultant-app__sort-icon icon icon--sort"
                  :class="sortIconClass(column.key)"
                  aria-hidden="true"
                ></span>
              </button>
              <span class="find-srf-consultant-app__mobile-column-header-text">
                {{ column.key === 'name' ? mobileColumnLabel : column.label }}
              </span>
            </div>
          </div>
          <div class="find-srf-consultant-app__rows">
            <div
            v-for="consultant in appData.consultants"
            :id="consultantFragment(consultant)"
            :key="consultant.id"
            class="find-srf-consultant-app__row"
            :class="{
              'find-srf-consultant-app__row--expanded': expandedConsultantId === consultant.id,
              'find-srf-consultant-app__row--non-expandable-desktop': consultant.authorizations?.length === 1 && !hasExpandableContactDetails(consultant),
            }"
            @click="toggleConsultantFromRow($event, consultant.id)"
          >
            <div class="find-srf-consultant-app__stack find-srf-consultant-app__stack--profile">
              <div class="find-srf-consultant-app__name">
                {{ consultant.name }}<span class="find-srf-consultant-app__mobile-city">{{ consultant.city ? `, ${consultant.city}` : '' }}</span>
              </div>
              <Transition
                :css="false"
                @enter="enterDetails"
                @leave="leaveDetails"
                @enter-cancelled="cancelDetailsAnimation"
                @leave-cancelled="cancelDetailsAnimation"
              >
                <div
                  v-show="expandedConsultantId === consultant.id"
                  :id="`consultant-profile-${consultant.id}`"
                  class="find-srf-consultant-app__contact find-srf-consultant-app__contact--profile"
                >
                  <a v-if="consultant.linkedInUrl" :href="consultant.linkedInUrl" target="_blank" rel="noopener noreferrer">
                    {{ translations.toLinkedIn }}
                  </a>
                </div>
              </Transition>
            </div>
            <div class="find-srf-consultant-app__stack find-srf-consultant-app__stack--personal">
              <div v-if="consultant.city" class="find-srf-consultant-app__city">{{ consultant.city }}</div>
              <Transition
                :css="false"
                @enter="enterDetails"
                @leave="leaveDetails"
                @enter-cancelled="cancelDetailsAnimation"
                @leave-cancelled="cancelDetailsAnimation"
              >
                <div
                  v-show="expandedConsultantId === consultant.id"
                  :id="`consultant-personal-${consultant.id}`"
                  class="find-srf-consultant-app__contact find-srf-consultant-app__contact--personal"
                >
                  <ContactReveal
                    v-if="consultant.hasEmail"
                    :contact-id="consultant.id"
                    :contact-name="consultant.name"
                    contact-type="email"
                    :translations="translations"
                  />
                  <ContactReveal
                    v-if="consultant.hasPhone"
                    :contact-id="consultant.id"
                    :contact-name="consultant.name"
                    contact-type="phone"
                    :translations="translations"
                  />
                </div>
              </Transition>
            </div>
            <div class="find-srf-consultant-app__stack find-srf-consultant-app__stack--company">
              <div class="find-srf-consultant-app__company">
                {{ consultant.company }}<span
                  v-if="expandedConsultantId !== consultant.id && consultant.authorizations?.length"
                  class="find-srf-consultant-app__mobile-authorizations"
                >, {{ authorizationSummary(consultant) }}</span>
              </div>
              <Transition
                :css="false"
                @enter="enterDetails"
                @leave="leaveDetails"
                @enter-cancelled="cancelDetailsAnimation"
                @leave-cancelled="cancelDetailsAnimation"
              >
                <div
                  v-show="expandedConsultantId === consultant.id"
                  :id="`consultant-company-${consultant.id}`"
                  class="find-srf-consultant-app__contact find-srf-consultant-app__contact--company"
                >
                  <a v-if="consultant.companyWebsite" :href="consultant.companyWebsite" target="_blank" rel="noopener noreferrer">
                    {{ websiteLabel(consultant.companyWebsite) }}
                  </a>
                </div>
              </Transition>
            </div>
            <Transition
              :css="false"
              @enter="enterDetails"
              @leave="leaveDetails"
              @enter-cancelled="cancelDetailsAnimation"
              @leave-cancelled="cancelDetailsAnimation"
            >
              <div v-if="expandedConsultantId !== consultant.id" class="find-srf-consultant-app__authorization-summary">
                <template v-if="consultant.authorizations?.length">
                  <span class="find-srf-consultant-app__check-icon icon icon--check" aria-hidden="true"></span>
                  <span class="find-srf-consultant-app__authorization-summary-text">
                    {{ authorizationSummary(consultant) }}
                  </span>
                </template>
                <span v-else aria-hidden="true">–</span>
              </div>
            </Transition>
            <button
              class="find-srf-consultant-app__toggle-button"
              type="button"
              :aria-expanded="expandedConsultantId === consultant.id"
              :aria-controls="`consultant-details-${consultant.id} consultant-profile-${consultant.id} consultant-personal-${consultant.id} consultant-company-${consultant.id}`"
              :aria-label="toggleLabel(consultant)"
              @click="toggleConsultant(consultant.id)"
            >
              <span
                class="find-srf-consultant-app__toggle-icon icon"
                :class="expandedConsultantId === consultant.id ? 'icon--chevron-up' : 'icon--chevron-down'"
                aria-hidden="true"
              ></span>
            </button>
            <Transition
              :css="false"
              @enter="enterDetails"
              @leave="leaveDetails"
              @enter-cancelled="cancelDetailsAnimation"
              @leave-cancelled="cancelDetailsAnimation"
            >
              <ul
                v-show="expandedConsultantId === consultant.id"
                :id="`consultant-details-${consultant.id}`"
                class="find-srf-consultant-app__authorizations"
              >
                <li v-for="authorization in consultant.authorizations ?? []" :key="authorization.id">
                  <span class="find-srf-consultant-app__check-icon icon icon--check" aria-hidden="true"></span>
                  {{ authorization.label }}
                </li>
              </ul>
            </Transition>
          </div>
          </div>
        </div>

        <p v-else-if="isSearchTooShort" class="find-srf-consultant-app__no-results">
          {{ translations.minimumSearchLength }}
        </p>
        <p v-else-if="hasAppliedSearchOrFilter" class="find-srf-consultant-app__no-results">
          {{ translations.noResults }}
        </p>
        <p v-else class="find-srf-consultant-app__no-results">{{ translations.initialSearch }}</p>

        <nav v-if="hasAppliedSearchOrFilter && totalPages > 1" :aria-label="translations.page">
          <ul class="pagination find-srf-consultant-app__pagination">
            <li
              class="pagination__item pagination__item--control pagination__item--previous"
              :class="{ 'pagination__item--disabled': currentPage === 1 }"
            >
              <span
                v-if="currentPage === 1"
                class="pagination__label"
                aria-disabled="true"
              >
                <span class="pagination__icon icon icon--chevron-left" aria-hidden="true"></span>
              </span>
              <a
                v-else
                class="pagination__link"
                :href="pageUrl(currentPage - 1)"
                :aria-label="translations.previousPage"
                @click.prevent="goToPage(currentPage - 1)"
              >
                <span class="pagination__icon icon icon--chevron-left" aria-hidden="true"></span>
              </a>
            </li>

            <li
              v-for="pageNumber in pageNumbers"
              :key="typeof pageNumber === 'number' ? pageNumber : pageNumber.key"
              class="pagination__item pagination__item--number"
              :class="{ 'pagination__item--active': currentPage === pageNumber }"
            >
              <button
                v-if="typeof pageNumber !== 'number'"
                class="pagination__link"
                type="button"
                :aria-label="translations.showAllPages"
                @click="expandPagination(pageNumber.firstHiddenPage)"
              >
                <span aria-hidden="true">…</span>
              </button>
              <span
                v-else-if="currentPage === pageNumber"
                class="pagination__label"
                aria-current="page"
              >
                {{ pageNumber }}
              </span>
              <a
                v-else-if="typeof pageNumber === 'number'"
                class="pagination__link"
                :href="pageUrl(pageNumber)"
                :ref="(element) => focusExpandedPage(element, pageNumber)"
                :aria-label="`${translations.page} ${pageNumber}`"
                @click.prevent="goToPage(pageNumber)"
              >
                {{ pageNumber }}
              </a>
            </li>

            <li
              class="pagination__item pagination__item--control pagination__item--next"
              :class="{ 'pagination__item--disabled': currentPage === totalPages }"
            >
              <span
                v-if="currentPage === totalPages"
                class="pagination__label"
                aria-disabled="true"
              >
                <span class="pagination__icon icon icon--chevron-right" aria-hidden="true"></span>
              </span>
              <a
                v-else
                class="pagination__link"
                :href="pageUrl(currentPage + 1)"
                :aria-label="translations.nextPage"
                @click.prevent="goToPage(currentPage + 1)"
              >
                <span class="pagination__icon icon icon--chevron-right" aria-hidden="true"></span>
              </a>
            </li>
          </ul>
        </nav>
      </section>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';
  import { debounce } from '@utils/debounce';
  import { useFetch } from '@utils/fetch-service';
  import LoadingSpinner from '@vue-components/LoadingSpinner.vue';
  import ContactReveal from './Components/ContactReveal/ContactReveal.vue';
  import type { IFindSrfConsultant, } from './interfaces/IFindSrfConsultant';
  import type { IFindSrfConsultantPageAppData, } from './interfaces/IFindSrfConsultantPageAppData';
  import type { IFindSrfConsultantSuggestions, } from './interfaces/IFindSrfConsultantSuggestions';

  type SortKey = 'name' | 'city' | 'company' | 'authorization';
  type SortDirection = 'ascending' | 'descending';
  type SearchSuggestion = { type: string; value: string };

  const minimumSearchLength = 3;
  const minimumLoadingDuration = 1200;
  const suggestionDebounceDuration = 300;
  const searchParameter = 'q';
  const typeParameter = 'type';
  const sortParameter = 'sort';
  const pageParameter = 'page';
  const props = defineProps<{
    pageId: number;
    locale: string;
  }>();

  const appData = ref<IFindSrfConsultantPageAppData | null>(null);
  const suggestionData = ref<IFindSrfConsultantSuggestions | null>(null);
  const isLoading = ref(true);
  const errorMessage = ref('');
  const draftSearch = ref('');
  const committedSearch = ref('');
  const onlyAuthorizedSalaryConsultants = ref(false);
  const onlyAuthorizedAccountingConsultants = ref(false);
  const onlyCertifiedBusinessAdvisors = ref(false);
  const onlyAuthorizedAgencies = ref(false);
  const currentPage = ref(1);
  const isPaginationExpanded = ref(false);
  const expandedConsultantId = ref<string | null>(null);
  const fragmentOnlyConsultantId = ref<string | null>(null);
  const detailAnimations = new WeakMap<HTMLElement, Animation>();
  const sortKey = ref<SortKey | null>(null);
  const sortDirection = ref<SortDirection>('ascending');
  const activeSuggestionIndex = ref(-1);
  const isSuggestionListOpen = ref(false);
  let suggestionCloseTimeout: number | undefined;
  let suggestionAbortController: AbortController | undefined;
  let pageToFocus: number | null = null;
  let latestDataRequestId = 0;
  let latestSuggestionRequestId = 0;
  let isUpdatingSearchState = false;
  const loadSuggestionsDebounced = debounce((...args: unknown[]) => {
    const [search, requestId] = args;

    if (typeof search === 'string'
      && typeof requestId === 'number'
      && requestId === latestSuggestionRequestId) {
      void loadSuggestions(search, requestId);
    }
  }, suggestionDebounceDuration);

  const translations = computed(() => appData.value!.translations);
  const selectedSort = computed({
    get: () => {
      const option = appData.value?.sortOptions.find((item) => item.key === sortKey.value
        && item.direction === sortDirection.value);

      return option ? `${option.key}-${option.direction}` : '';
    },
    set: (value: string) => {
      const option = appData.value?.sortOptions.find((item) => `${item.key}-${item.direction}` === value);

      if (option) {
        sortKey.value = option.key;
        sortDirection.value = option.direction;
        resetResultView();
        updateUrl();
        void loadAppData();
      }
    },
  });
  const loadingLabel = computed(() => appData.value?.translations.loading);

  const columns = computed<{ key: SortKey; label: string }[]>(() => [
    { key: 'name', label: translations.value.name },
    { key: 'city', label: translations.value.city },
    { key: 'company', label: translations.value.company },
    { key: 'authorization', label: translations.value.authorization },
  ]);

  const mobileColumnLabel = computed(() => columns.value.find((column) => column.key === sortKey.value)?.label
    ?? translations.value.name);

  const hasActiveFilters = computed(() => Boolean(
    draftSearch.value
    || committedSearch.value
    || hasSelectedConsultantFilters.value,
  ));
  const committedSearchLength = computed(() => searchWords(committedSearch.value).join('').length);
  const isSearchTooShort = computed(() => committedSearchLength.value > 0
    && committedSearchLength.value < minimumSearchLength);
  const hasAppliedSearchOrFilter = computed(() => Boolean(
    !isSearchTooShort.value
    && (committedSearchLength.value
      || hasSelectedConsultantFilters.value
      || expandedConsultantId.value),
  ));
  const hasSelectedConsultantFilters = computed(() => Boolean(
    onlyAuthorizedSalaryConsultants.value
    || onlyAuthorizedAccountingConsultants.value
    || onlyCertifiedBusinessAdvisors.value
    || onlyAuthorizedAgencies.value,
  ));

  const searchSuggestions = computed<SearchSuggestion[]>(() => {
    const data = suggestionData.value;

    if (!data) {
      return [];
    }

    const suggestions = (values: string[], type: string) => values
      .map((value) => ({ type, value }));

    return [
      ...suggestions(data.companySuggestions, translations.value.company),
      ...suggestions(data.citySuggestions, translations.value.city),
    ];
  });

  const totalPages = computed(() => appData.value?.totalPages ?? 1);
  const pageNumbers = computed(() => {
    const total = totalPages.value;

    if (total <= 5 || isPaginationExpanded.value) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    let start = currentPage.value;
    let end = currentPage.value;

    if (currentPage.value <= 3) {
      start = 2;
      end = 3;
    } else if (currentPage.value >= total - 2) {
      start = total - 2;
      end = total - 1;
    }
    const pages: Array<number | { key: string; firstHiddenPage: number }> = [1];

    if (start > 2) {
      pages.push({ key: 'previous-gap', firstHiddenPage: 2 });
    }

    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    if (end < total - 1) {
      pages.push({ key: 'next-gap', firstHiddenPage: end + 1 });
    }

    pages.push(total);
    return pages;
  });
  const resultsSummary = computed(() => {
    const total = appData.value?.totalCount ?? 0;
    const size = appData.value?.pageSize ?? 16;
    const first = total ? ((currentPage.value - 1) * size) + 1 : 0;
    const last = Math.min(currentPage.value * size, total);

    return formatTranslation(translations.value.showingResults, first, last, total);
  });

  watch(
    [
      onlyAuthorizedSalaryConsultants,
      onlyAuthorizedAccountingConsultants,
      onlyCertifiedBusinessAdvisors,
      onlyAuthorizedAgencies,
    ],
    () => {
      if (isUpdatingSearchState) {
        return;
      }

      resetResultView();
      updateUrl();
      void loadAppData();
    },
    { flush: 'sync' },
  );

  watch(searchSuggestions, () => {
    activeSuggestionIndex.value = -1;
  });

  onBeforeMount(async () => {
    applySearchStateFromUrl();
    const loadingStartedAt = performance.now();

    await loadAppData();
    const remainingLoadingTime = minimumLoadingDuration - (performance.now() - loadingStartedAt);

    if (remainingLoadingTime > 0) {
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, remainingLoadingTime);
      });
    }

    isLoading.value = false;
  });

  window.addEventListener('popstate', handlePopState);
  window.addEventListener('hashchange', handleHashChange);

  onBeforeUnmount(() => {
    window.removeEventListener('popstate', handlePopState);
    window.removeEventListener('hashchange', handleHashChange);
    window.clearTimeout(suggestionCloseTimeout);
    cancelSuggestionSearch();
  });

  function applySearch() {
    const search = draftSearch.value.trim();

    draftSearch.value = search;
    committedSearch.value = search;
    cancelSuggestionSearch();
    closeSuggestionsImmediately();
    resetResultView();
    updateUrl();
    void loadAppData();
  }

  function applySearchFromInput(event: Event) {
    draftSearch.value = (event.target as HTMLInputElement).value;
    applySearch();
  }

  function moveSuggestion(direction: number) {
    if (!searchSuggestions.value.length) {
      return;
    }

    isSuggestionListOpen.value = true;
    activeSuggestionIndex.value = (activeSuggestionIndex.value + direction + searchSuggestions.value.length)
      % searchSuggestions.value.length;
  }

  function handleSuggestionInput() {
    window.clearTimeout(suggestionCloseTimeout);
    isSuggestionListOpen.value = true;
    scheduleSuggestionSearch();
  }

  function selectActiveSuggestion(event: KeyboardEvent) {
    const suggestion = searchSuggestions.value[activeSuggestionIndex.value];

    if (!suggestion || !isSuggestionListOpen.value) {
      return;
    }

    event.preventDefault();
    selectSuggestion(suggestion.value);
  }

  function selectSuggestion(value: string) {
    const search = replaceCurrentSearchWord(draftSearch.value, value);

    draftSearch.value = search;
    committedSearch.value = search;
    cancelSuggestionSearch();
    closeSuggestionsImmediately();
    resetResultView();
    updateUrl();
    void loadAppData();
  }

  function closeSuggestions() {
    suggestionCloseTimeout = window.setTimeout(closeSuggestionsImmediately, 150);
  }

  function closeSuggestionsImmediately() {
    window.clearTimeout(suggestionCloseTimeout);
    isSuggestionListOpen.value = false;
    activeSuggestionIndex.value = -1;
  }

  function scheduleSuggestionSearch() {
    suggestionAbortController?.abort();
    suggestionAbortController = undefined;
    suggestionData.value = null;
    const requestId = ++latestSuggestionRequestId;

    if (currentSearchWord(draftSearch.value).length < minimumSearchLength) {
      return;
    }

    const search = draftSearch.value;
    loadSuggestionsDebounced(search, requestId);
  }

  async function loadSuggestions(search: string, requestId: number) {
    const { get } = useFetch('/findsrfconsultantapp/suggestions');
    const abortController = new AbortController();

    // If a new request is made before the previous one completes, abort the previous request
    suggestionAbortController = abortController;

    try {
      const data = await get<IFindSrfConsultantSuggestions>({
        pageId: props.pageId,
        locale: props.locale,
        q: search,
      }, { signal: abortController.signal });

      if (requestId === latestSuggestionRequestId) {
        suggestionData.value = data;
      }
    } catch (error) {
      if (requestId === latestSuggestionRequestId && !(error instanceof DOMException && error.name === 'AbortError')) {
        suggestionData.value = null;
      }
    } finally {
      if (requestId === latestSuggestionRequestId) {
        suggestionAbortController = undefined;
      }
    }
  }

  function cancelSuggestionSearch() {
    suggestionAbortController?.abort();
    suggestionAbortController = undefined;
    suggestionData.value = null;
    latestSuggestionRequestId += 1;
  }

  function clearFilters() {
    isUpdatingSearchState = true;
    draftSearch.value = '';
    committedSearch.value = '';
    onlyAuthorizedSalaryConsultants.value = false;
    onlyAuthorizedAccountingConsultants.value = false;
    onlyCertifiedBusinessAdvisors.value = false;
    onlyAuthorizedAgencies.value = false;
    sortKey.value = null;
    sortDirection.value = 'ascending';
    fragmentOnlyConsultantId.value = null;
    cancelSuggestionSearch();
    closeSuggestionsImmediately();
    resetResultView();
    isUpdatingSearchState = false;

    const url = new URL(window.location.href);
    url.search = '';
    url.hash = '';
    window.history.replaceState({}, '', url.toString());
    void loadAppData();
  }

  function resetResultView() {
    currentPage.value = 1;
    isPaginationExpanded.value = false;
    expandedConsultantId.value = null;
  }

  async function loadAppData() {
    const requestId = ++latestDataRequestId;
    const { get } = useFetch('/findsrfconsultantapp/data');

    errorMessage.value = '';

    try {
      const data = await get<IFindSrfConsultantPageAppData>({
        pageId: props.pageId,
        locale: props.locale,
        page: currentPage.value,
        q: committedSearch.value || undefined,
        types: selectedConsultantTypes().join(',') || undefined,
        sort: sortKey.value ? `${sortKey.value}-${sortDirection.value}` : undefined,
        consultant: fragmentOnlyConsultantId.value ?? undefined,
      });

      if (requestId !== latestDataRequestId) {
        return;
      }

      appData.value = data;
      currentPage.value = data.currentPage;
      applyExpandedConsultantFromHash();
      updateUrl();
    } catch (error) {
      if (requestId === latestDataRequestId) {
        errorMessage.value = error instanceof Error ? error.message : '';
      }
    }
  }

  function selectedConsultantTypes() {
    return [
      { value: 'salary', selected: onlyAuthorizedSalaryConsultants.value },
      { value: 'accounting', selected: onlyAuthorizedAccountingConsultants.value },
      { value: 'business-advisor', selected: onlyCertifiedBusinessAdvisors.value },
      { value: 'agency', selected: onlyAuthorizedAgencies.value },
    ]
      .filter((type) => type.selected)
      .map((type) => type.value);
  }

  function handlePopState() {
    applySearchStateFromUrl();
    void loadAppData();
  }

  function handleHashChange() {
    const fragment = decodeURIComponent(window.location.hash.substring(1));
    const isFragmentOnly = window.location.search === '' && fragment.startsWith('consultant-');

    if (isFragmentOnly && fragment !== fragmentOnlyConsultantId.value) {
      fragmentOnlyConsultantId.value = fragment;
      currentPage.value = 1;
      void loadAppData();
      return;
    }

    applyExpandedConsultantFromHash();
  }

  function applySearchStateFromUrl() {
    isUpdatingSearchState = true;
    const parameters = new URLSearchParams(window.location.search);
    const types = parameters.getAll(typeParameter);
    const sort = parameters.get(sortParameter);
    const sortKeys: SortKey[] = ['name', 'city', 'company', 'authorization'];
    const sortDirections: SortDirection[] = ['ascending', 'descending'];
    const key = sortKeys.find((key) => sortDirections.some((direction) => sort === `${key}-${direction}`));

    sortKey.value = key ?? null;
    sortDirection.value = key && sort === `${key}-descending` ? 'descending' : 'ascending';

    draftSearch.value = (parameters.get(searchParameter) ?? '').trim();
    committedSearch.value = draftSearch.value;
    onlyAuthorizedSalaryConsultants.value = types.includes('salary');
    onlyAuthorizedAccountingConsultants.value = types.includes('accounting');
    onlyCertifiedBusinessAdvisors.value = types.includes('business-advisor');
    onlyAuthorizedAgencies.value = types.includes('agency');
    resetResultView();
    const page = Number.parseInt(parameters.get(pageParameter) ?? '', 10);
    currentPage.value = Number.isFinite(page) && page > 0 ? page : 1;
    const fragment = decodeURIComponent(window.location.hash.substring(1));
    fragmentOnlyConsultantId.value = window.location.search === '' && fragment.startsWith('consultant-')
      ? fragment
      : null;
    isUpdatingSearchState = false;
  }

  function updateUrl(pushState = false) {
    const url = new URL(window.location.href);

    const sort = sortKey.value ? `${sortKey.value}-${sortDirection.value}` : '';

    setUrlParameter(url.searchParams, sortParameter, sort);
    setUrlParameter(url.searchParams, searchParameter, committedSearch.value);
    setUrlParameter(
      url.searchParams,
      pageParameter,
      committedSearch.value || hasSelectedConsultantFilters.value ? String(currentPage.value) : '',
    );
    url.searchParams.delete(typeParameter);

    selectedConsultantTypes().forEach((type) => {
      url.searchParams.append(typeParameter, type);
    });

    const expandedConsultant = appData.value?.consultants.find((consultant) => consultant.id === expandedConsultantId.value);
    const expandedConsultantFragment = expandedConsultant ? consultantFragment(expandedConsultant) : '';

    if (expandedConsultantFragment) {
      url.hash = expandedConsultantFragment;
    } else if (appData.value && url.hash.startsWith('#consultant-')) {
      url.hash = '';
    }
    fragmentOnlyConsultantId.value = url.search === '' && url.hash.startsWith('#consultant-')
      ? decodeURIComponent(url.hash.substring(1))
      : null;

    window.history[pushState ? 'pushState' : 'replaceState']({}, '', url.toString());
  }

  function setUrlParameter(parameters: URLSearchParams, name: string, value: string) {
    if (value) {
      parameters.set(name, value);
    } else {
      parameters.delete(name);
    }
  }

  function sortBy(key: SortKey) {
    if (sortKey.value === key) {
      sortDirection.value = sortDirection.value === 'ascending' ? 'descending' : 'ascending';
    } else {
      sortKey.value = key;
      sortDirection.value = 'ascending';
    }

    resetResultView();
    updateUrl();
    void loadAppData();
  }

  function sortLabel(label: string, key: SortKey) {
    const direction = sortKey.value === key && sortDirection.value === 'ascending'
      ? translations.value.sortDescending
      : translations.value.sortAscending;

    return `${label}: ${direction}`;
  }

  function sortIconClass(key: SortKey) {
    if (sortKey.value !== key) {
      return '';
    }

    return sortDirection.value === 'ascending'
      ? 'find-srf-consultant-app__sort-icon--ascending'
      : 'find-srf-consultant-app__sort-icon--descending';
  }

  function enterDetails(element: Element, done: () => void) {
    animateDetails(element as HTMLElement, done, true);
  }

  function leaveDetails(element: Element, done: () => void) {
    animateDetails(element as HTMLElement, done, false);
  }

  function cancelDetailsAnimation(element: Element) {
    const animation = detailAnimations.get(element as HTMLElement);

    if (animation) {
      animation.commitStyles();
      animation.cancel();
      detailAnimations.delete(element as HTMLElement);
    }
  }

  function animateDetails(element: HTMLElement, done: () => void, expanding: boolean) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      clearDetailsAnimationStyles(element);
      done();
      return;
    }

    const currentStyle = getComputedStyle(element);

    if (currentStyle.display === 'none') {
      clearDetailsAnimationStyles(element);
      done();
      return;
    }

    const currentHeight = currentStyle.height;
    const currentOpacity = currentStyle.opacity;
    const currentMargin = currentStyle.marginTop;
    const interrupted = Boolean(element.style.height);

    clearDetailsAnimationStyles(element);
    const targetStyle = getComputedStyle(element);
    const expandedHeight = targetStyle.height;
    const expandedMargin = targetStyle.marginTop;
    const animation = element.animate(
      [
        {
          height: expanding && !interrupted ? '0px' : currentHeight,
          opacity: expanding && !interrupted ? 0 : currentOpacity,
          marginTop: expanding && !interrupted ? '0px' : currentMargin,
          overflow: 'hidden',
        },
        {
          height: expanding ? expandedHeight : '0px',
          opacity: expanding ? 1 : 0,
          marginTop: expanding ? expandedMargin : '0px',
          overflow: 'hidden',
        },
      ],
      { duration: 250, easing: 'ease-in-out', fill: 'both' },
    );

    detailAnimations.set(element, animation);
    animation.onfinish = () => {
      detailAnimations.delete(element);
      done();
      animation.cancel();
      clearDetailsAnimationStyles(element);
    };
  }

  function clearDetailsAnimationStyles(element: HTMLElement) {
    ['height', 'opacity', 'margin-top', 'overflow'].forEach((property) => element.style.removeProperty(property));
  }

  function toggleConsultant(consultantId: string) {
    expandedConsultantId.value = expandedConsultantId.value === consultantId ? null : consultantId;
    updateUrl();
  }

  function toggleConsultantFromRow(event: MouseEvent, consultantId: string) {
    if ((event.target as HTMLElement).closest('a, button')) {
      return;
    }

    toggleConsultant(consultantId);
  }

  function hasExpandableContactDetails(consultant: IFindSrfConsultant) {
    return Boolean(
      consultant.companyWebsite
      || consultant.hasEmail
      || consultant.hasPhone
      || consultant.linkedInUrl,
    );
  }

  function applyExpandedConsultantFromHash() {
    const fragment = decodeURIComponent(window.location.hash.substring(1));

    // Keep the fragment while the consultant data is still loading.
    if (!appData.value) {
      return;
    }

    const matchingConsultants = appData.value.consultants
      .filter((consultant) => consultantFragment(consultant) === fragment);
    const consultant = matchingConsultants.length === 1 ? matchingConsultants[0] : null;
    fragmentOnlyConsultantId.value = window.location.search === '' && consultant ? fragment : null;
    expandedConsultantId.value = consultant?.id ?? null;

    if (consultant) {
      nextTick(() => document.getElementById(fragment)?.scrollIntoView());
    }
  }

  function toggleLabel(consultant: IFindSrfConsultant) {
    const action = expandedConsultantId.value === consultant.id
      ? translations.value.collapseConsultant
      : translations.value.expandConsultant;

    return `${action} ${consultant.name}`;
  }

  function consultantFragment(consultant: IFindSrfConsultant) {
    return `consultant-${consultant.name}`
      .trim()
      .replace(/\s+/g, '-')
      .toLocaleLowerCase(props.locale);
  }

  function expandPagination(firstHiddenPage: number) {
    pageToFocus = firstHiddenPage;
    isPaginationExpanded.value = true;
  }

  function focusExpandedPage(element: unknown, pageNumber: number) {
    if (pageNumber === pageToFocus && element instanceof HTMLAnchorElement) {
      element.focus();
      pageToFocus = null;
    }
  }

  function pageUrl(pageNumber: number) {
    const url = new URL(window.location.href);

    url.searchParams.set(pageParameter, String(pageNumber));
    return url.toString();
  }

  function goToPage(pageNumber: number) {
    if (pageNumber < 1 || pageNumber > totalPages.value) {
      return;
    }

    currentPage.value = pageNumber;
    isPaginationExpanded.value = false;
    expandedConsultantId.value = null;
    updateUrl(true);
    void loadAppData();
  }

  function authorizationSummary(consultant: IFindSrfConsultant) {
    return (consultant.authorizations ?? [])
      .map((authorization) => abbreviatedAuthorizationLabel(authorization.label))
      .join(', ');
  }

  // This function takes a label string and abbreviates the first word to its first three characters followed by a period.
  // Ex: "Auktoriserad redovisningskonsult" becomes "Auk. redovisningskonsult"
  function abbreviatedAuthorizationLabel(label: string) {
    const [firstWord, ...remainingWords] = label.trim().split(/\s+/);

    return firstWord
      ? [`${firstWord.slice(0, 3)}.`, ...remainingWords].join(' ')
      : label;
  }

  function websiteLabel(url: string) {
    return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }

  function formatTranslation(template: string, ...values: Array<string | number>) {
    return values.reduce<string>(
      (formatted, value, index) => formatted.replace(`{${index}}`, String(value)),
      template,
    );
  }

  function searchWords(search: string) {
    return search
      .replace(/-/g, ' ')
      .split(/[\s,]+/)
      .map(normalizeSearchValue)
      .filter(Boolean);
  }

  function currentSearchWord(search: string) {
    return searchWords(search).at(-1) ?? '';
  }

  function replaceCurrentSearchWord(search: string, value: string) {
    return search.replace(/[^\s,]*$/, value);
  }

  function normalizeSearchValue(value: string) {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase(props.locale);
  }
</script>
